import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { apiRequest, readUISettings } from "@/lib/api";
import { AUTH_USER_STORAGE_KEY } from "@/lib/auth-storage";
import {
  readCloudWalletDashboardCache,
  readCloudWalletAccountsPublic,
  type CloudWalletSummaryResponse,
} from "@/lib/cloud-wallet";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import { readPageCache, writePageCache } from "@/lib/page-cache";

type JobRecord = {
  state?: string;
  created_at?: string;
  updated_at?: string;
  vm_ready_at?: string;
  vm_terminated_at?: string;
  billed_total_value?: number;
  client_id?: string;
  request?: {
    job_id?: string;
    client_id?: string;
    selected_provider?: string;
    selected_region?: string;
    selected_gpu_type?: string;
    selected_gpu_count?: number;
  };
  selected_provider?: string;
  selected_region?: string;
  selected_gpu_type?: string;
  selected_gpu_count?: number;
  provisioned_provider?: string;
  provisioned_region?: string;
  provisioned_gpu_type?: string;
  provisioned_gpu_count?: number;
};

type MarketOffer = {
  provider_name?: string;
  provider_slug?: string;
  source?: string;
  region_name?: string;
  gpu?: string;
  flavor_name?: string;
  stock_available?: number;
  price_per_hour?: number;
  currency?: string;
};

type AckRecord = {
  state?: string;
  spoke_id?: string;
  spoke_peer_id?: string;
  last_state_change_at?: string;
  ack_payload?: {
    spoke_legal_entity?: { legal_name?: string };
    agreed_regions?: string[];
  };
};

type RuntimeHealth = {
  hub?: boolean;
  spoke?: boolean;
  hub_id?: string;
  daemon?: {
    active_mode?: string;
  };
};

type CapacityEntry = {
  signal?: {
    spoke_id?: string;
    avail_count?: number;
    region?: string;
    gpu_type?: string;
    price_gpu_hr_value?: number;
    price_gpu_hr?: number;
  };
};

type WalletAccount = {
  account_id?: string;
  display_name?: string;
  provider_name?: string;
  provider_slug?: string;
  credit?: number;
  currency?: string;
  status?: string;
  fetched_at_utc?: string;
};

type WalletSummaryPayload = {
  total_credits_by_currency?: Array<{ currency?: string; credit?: number }>;
  accounts?: WalletAccount[];
};

type StorageVolume = {
  id?: number;
  name?: string;
  size_gb?: number;
  volume_type?: string;
  region_name?: string;
  updated_at?: string;
  status?: string;
};

type StorageVolumesPayload = {
  volumes?: StorageVolume[];
};

type BillingUsagePayload = {
  summary?: {
    gpu_hours?: number;
    charge_value_total?: number;
    charges_by_currency?: Record<string, number>;
  };
};

type CreditsBalancePayload = {
  available_credit_micros?: number;
  reserved_credit_micros?: number;
  spent_credit_micros?: number;
  earned_credit_micros?: number;
  pending_payout_credit_micros?: number;
};

type ActivityRow = { time: string; label: string };
type IntegrationRunRow = {
  id: string;
  state: "running" | "queued" | "completed" | "failed";
  provider: string;
  gpu: string;
  region: string;
  spent: number;
  updated: string;
};

type PartnerRow = {
  name: string;
  region: string;
  availability: string;
  status: "connected" | "pending" | "error";
  lastChecked: string;
};

type BidRow = {
  gpu: string;
  source: string;
  location: string;
  capacity: number;
  price: number;
  currency: string;
};

type WalletRow = {
  account: string;
  provider: string;
  credit: number;
  currency: string;
  bestRate: number | null;
  updated: string;
};

type StorageRow = {
  id: string;
  region: string;
  type: string;
  gb: number;
  lastWrite: string;
};

type WeeklyRow = { day: string; jobs: number };
type ProviderLiveRow = { provider: string; bids: number };
type UsageByProviderRow = { provider: string; spend: number };
type StorageByRegionRow = { region: string; gb: number };
type ExecutionRow = { name: string; value: number; color: string };

export type DashboardLiveMetrics = {
  totalJobs: number;
  jobsRunning: number;
  jobsQueued: number;
  jobsCompleted: number;
  jobsFailed: number;
  settledJobs: number;
  successRate: number;
  avgCompletedJobCostUSD: number;
  avgRunDurationMin: number;
  usageSpendUSD: number;
  walletTotalUSD: number;
  hubCreditsGNRL: number;
  billedGpuHours: number;
  usdPerGpuHour: number;
  liveBids: number;
  providersOffered: number;
  hubCount: number;
  connectionCount: number;
  activeSpokeCount: number;
  liveSpokeCount: number;
  storageTotalGB: number;
  volumeCount: number;
  bidsPerProvider: number;
};

export type DashboardLiveData = {
  loading: boolean;
  error: string;
  fetchedAt: string;
  metrics: DashboardLiveMetrics;
  providerLiveRows: ProviderLiveRow[];
  executionRows: ExecutionRow[];
  weeklyRows: WeeklyRow[];
  usageByProviderRows: UsageByProviderRow[];
  storageByRegionRows: StorageByRegionRow[];
  peakDay: WeeklyRow;
  integrationRuns: IntegrationRunRow[];
  partners: PartnerRow[];
  liveBids: BidRow[];
  walletAccounts: WalletRow[];
  activityFeed: ActivityRow[];
  storageVolumes: StorageRow[];
  refresh: () => Promise<void>;
};

const EMPTY_METRICS: DashboardLiveMetrics = {
  totalJobs: 0,
  jobsRunning: 0,
  jobsQueued: 0,
  jobsCompleted: 0,
  jobsFailed: 0,
  settledJobs: 0,
  successRate: 0,
  avgCompletedJobCostUSD: 0,
  avgRunDurationMin: 0,
  usageSpendUSD: 0,
  walletTotalUSD: 0,
  hubCreditsGNRL: 0,
  billedGpuHours: 0,
  usdPerGpuHour: 0,
  liveBids: 0,
  providersOffered: 0,
  hubCount: 0,
  connectionCount: 0,
  activeSpokeCount: 0,
  liveSpokeCount: 0,
  storageTotalGB: 0,
  volumeCount: 0,
  bidsPerProvider: 0,
};

const EMPTY_DATA: Omit<DashboardLiveData, "refresh"> = {
  loading: true,
  error: "",
  fetchedAt: "",
  metrics: EMPTY_METRICS,
  providerLiveRows: [],
  executionRows: [],
  weeklyRows: [],
  usageByProviderRows: [],
  storageByRegionRows: [],
  peakDay: { day: "—", jobs: 0 },
  integrationRuns: [],
  partners: [],
  liveBids: [],
  walletAccounts: [],
  activityFeed: [],
  storageVolumes: [],
};

const DASHBOARD_LIVE_PAGE_CACHE_KEY = "dashboard-live";
const DASHBOARD_LIVE_PAGE_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

function initialDashboardLiveData(): Omit<DashboardLiveData, "refresh"> {
  const cached = readPageCache<Omit<DashboardLiveData, "refresh">>(
    DASHBOARD_LIVE_PAGE_CACHE_KEY,
    DASHBOARD_LIVE_PAGE_CACHE_MAX_AGE_MS,
  );
  if (!cached?.data?.metrics) return EMPTY_DATA;
  return {
    ...EMPTY_DATA,
    ...cached.data,
    loading: true,
    error: "",
  };
}

function cacheDashboardLiveData(data: Omit<DashboardLiveData, "refresh">) {
  writePageCache(DASHBOARD_LIVE_PAGE_CACHE_KEY, {
    ...data,
    loading: false,
    error: "",
  });
}

export function dashboardRefreshFailureState(
  previous: Omit<DashboardLiveData, "refresh">,
  error: string,
): Omit<DashboardLiveData, "refresh"> {
  return {
    ...previous,
    loading: false,
    error,
  };
}

export function dashboardHostedEmptyState(
  previous: Omit<DashboardLiveData, "refresh">,
  consecutiveEmptyRefreshes: number,
  fetchedAt = new Date().toISOString(),
): Omit<DashboardLiveData, "refresh"> {
  if (previous.fetchedAt && consecutiveEmptyRefreshes < 2) {
    return {
      ...previous,
      loading: false,
      error: "",
    };
  }
  return {
    ...EMPTY_DATA,
    loading: false,
    fetchedAt,
  };
}

const DashboardLiveContext = createContext<DashboardLiveData | null>(null);

function safeNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseTime(value?: string): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
}

function relTime(value?: string): string {
  const t = parseTime(value);
  if (!t) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function readStoredAuthUserID(): string {
  if (typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { id?: unknown };
    return String(parsed?.id || "").trim();
  } catch {
    return "";
  }
}

function jobStateKind(raw?: string): "running" | "queued" | "completed" | "failed" {
  const s = String(raw || "").toUpperCase();
  if (s === "RUNNING") return "running";
  if (s === "COMPLETED") return "completed";
  if (s === "FAILED") return "failed";
  return "queued";
}

function normalizeScopeKey(raw?: string): string {
  return String(raw || "").trim().toLowerCase();
}

function jobClientID(job: JobRecord): string {
  return String(job.request?.client_id || job.client_id || "").trim();
}

function buildHostedWalletLiveData(raw: {
  accounts: Awaited<ReturnType<typeof readCloudWalletAccountsPublic>>;
  summary: CloudWalletSummaryResponse | null;
}) {
  const snapshots = Array.isArray(raw.summary?.accounts) ? raw.summary.accounts : [];
  const snapshotByID = new Map(snapshots.map((snapshot) => [String(snapshot.account_id || "").trim(), snapshot]));
  const walletAccounts: WalletRow[] = raw.accounts.map((account) => {
    const snapshot = snapshotByID.get(String(account.id || "").trim());
    return {
      account: account.display_name,
      provider: account.provider_name,
      credit: Number(snapshot?.credit || 0),
      currency: String(snapshot?.currency || account.currency_hint || "USD").trim().toUpperCase() || "USD",
      bestRate: null,
      updated: relTime(snapshot?.fetched_at_utc || account.updated_at),
    };
  });

  const walletTotalUSD = walletAccounts
    .filter((row) => row.currency === "USD")
    .reduce((sum, row) => sum + row.credit, 0);
  const launchable = snapshots.filter((snapshot) => !!snapshot.can_create_instance).length;
  const failed = snapshots.filter((snapshot) => String(snapshot.status || "") !== "ok").length;
  const activityFeed: ActivityRow[] = snapshots.map((snapshot) => ({
    time: relTime(snapshot.fetched_at_utc),
    label: String(snapshot.status || "") === "ok"
      ? `${snapshot.provider_name} wallet refreshed`
      : `${snapshot.provider_name} wallet check failed`,
  }));

  return {
    metrics: {
      ...EMPTY_METRICS,
      walletTotalUSD,
      liveBids: launchable,
      providersOffered: new Set(walletAccounts.map((row) => row.provider)).size,
      connectionCount: walletAccounts.length,
      jobsFailed: failed,
    } as DashboardLiveMetrics,
    walletAccounts,
    activityFeed,
  };
}

function buildLiveData(raw: {
  jobs: JobRecord[];
  activeClientID?: string;
  offers: MarketOffer[];
  acks: AckRecord[];
  capacityEntries: CapacityEntry[];
  runtimeHealth: RuntimeHealth;
  walletAccounts: WalletAccount[];
  walletTotals: Array<{ currency?: string; credit?: number }>;
  storageVolumes: StorageVolume[];
  billingUsageSummary: BillingUsagePayload["summary"];
  creditsBalance: CreditsBalancePayload;
}): Omit<DashboardLiveData, "refresh" | "loading" | "error" | "fetchedAt"> {
  const activeClientID = normalizeScopeKey(raw.activeClientID);
  const jobsSource = Array.isArray(raw.jobs) ? raw.jobs : [];
  const jobs = activeClientID
    ? jobsSource.filter((j) => normalizeScopeKey(jobClientID(j)) === activeClientID)
    : jobsSource;
  const offers = (Array.isArray(raw.offers) ? raw.offers : []).filter((o) => safeNumber(o.stock_available) > 0 && safeNumber(o.price_per_hour) > 0);
  const acks = (Array.isArray(raw.acks) ? raw.acks : []).filter((a) => String(a.state || "").toUpperCase() !== "REVOKED");
  const capacityEntries = Array.isArray(raw.capacityEntries) ? raw.capacityEntries : [];
  const walletAccountsRaw = Array.isArray(raw.walletAccounts) ? raw.walletAccounts : [];
  const walletTotals = Array.isArray(raw.walletTotals) ? raw.walletTotals : [];
  const storageVolumesRaw = Array.isArray(raw.storageVolumes) ? raw.storageVolumes : [];

  const jobsRunning = jobs.filter((j) => String(j.state || "").toUpperCase() === "RUNNING").length;
  const jobsQueued = jobs.filter((j) => jobStateKind(j.state) === "queued").length;
  const jobsCompleted = jobs.filter((j) => String(j.state || "").toUpperCase() === "COMPLETED").length;
  const jobsFailed = jobs.filter((j) => String(j.state || "").toUpperCase() === "FAILED").length;
  const settledJobs = jobsCompleted + jobsFailed;

  const completedCosts = jobs
    .filter((j) => String(j.state || "").toUpperCase() === "COMPLETED")
    .map((j) => safeNumber(j.billed_total_value))
    .filter((v) => v > 0);
  const avgCompletedJobCostUSD = completedCosts.length
    ? completedCosts.reduce((a, b) => a + b, 0) / completedCosts.length
    : 0;

  const runDurationsMin = jobs
    .filter((j) => settledJobs > 0 && (String(j.state || "").toUpperCase() === "COMPLETED" || String(j.state || "").toUpperCase() === "FAILED"))
    .map((j) => {
      const start = parseTime(j.vm_ready_at || j.created_at);
      const end = parseTime(j.vm_terminated_at || j.updated_at);
      if (!start || !end || end <= start) return 0;
      return (end - start) / 60000;
    })
    .filter((v) => v > 0);
  const avgRunDurationMin = runDurationsMin.length
    ? runDurationsMin.reduce((a, b) => a + b, 0) / runDurationsMin.length
    : 0;

  const usageSpendFromJobs = jobs.reduce((sum, j) => sum + safeNumber(j.billed_total_value), 0);
  const usageSpendFromBilling = safeNumber(raw.billingUsageSummary?.charge_value_total);
  const usageSpendUSD = usageSpendFromBilling > 0 ? usageSpendFromBilling : usageSpendFromJobs;

  const walletTotalFromTotalsUSD = walletTotals
    .filter((row) => String(row.currency || "").toUpperCase() === "USD")
    .reduce((sum, row) => sum + safeNumber(row.credit), 0);
  const walletTotalFromAccountsUSD = walletAccountsRaw
    .filter((row) => String(row.currency || "USD").toUpperCase() === "USD")
    .reduce((sum, row) => sum + safeNumber(row.credit), 0);
  const walletTotalUSD = walletTotalFromTotalsUSD > 0 ? walletTotalFromTotalsUSD : walletTotalFromAccountsUSD;

  const creditsAvailable = safeNumber(raw.creditsBalance.available_credit_micros);
  const hubCreditsGNRL = creditsAvailable / 1_000_000;

  const billedGpuHours = safeNumber(raw.billingUsageSummary?.gpu_hours);
  const usdPerGpuHour = billedGpuHours > 0 ? usageSpendUSD / billedGpuHours : 0;
  const providersOffered = new Set(offers.map((o) => String(o.provider_slug || o.source || o.provider_name || "").toLowerCase()).filter(Boolean)).size;
  const mode = String(raw.runtimeHealth?.daemon?.active_mode || "").toLowerCase();
  const hubID = String(raw.runtimeHealth?.hub_id || "").trim();
  const hubCount = raw.runtimeHealth?.hub === true || mode.includes("hub") || hubID ? 1 : 0;
  const activeSpokeIDs = new Set(
    acks
      .filter((a) => String(a.state || "").toUpperCase() === "ACTIVE")
      .map((a) => String(a.spoke_id || a.spoke_peer_id || "").trim())
      .filter(Boolean),
  );
  const liveSpokeIDs = new Set(
    capacityEntries
      .map((c) => ({
        spokeID: String(c.signal?.spoke_id || "").trim(),
        availCount: safeNumber(c.signal?.avail_count),
      }))
      .filter((row) => row.spokeID && row.availCount > 0)
      .map((row) => row.spokeID),
  );
  const connectionCount = acks.length;
  const activeSpokeCount = activeSpokeIDs.size;
  const liveSpokeCount = liveSpokeIDs.size;
  const storageTotalGB = storageVolumesRaw.reduce((sum, v) => sum + safeNumber(v.size_gb), 0);
  const volumeCount = storageVolumesRaw.length;
  const liveBidsCount = offers.length;

  const metrics: DashboardLiveMetrics = {
    totalJobs: jobs.length,
    jobsRunning,
    jobsQueued,
    jobsCompleted,
    jobsFailed,
    settledJobs,
    successRate: settledJobs > 0 ? (jobsCompleted / settledJobs) * 100 : 0,
    avgCompletedJobCostUSD,
    avgRunDurationMin,
    usageSpendUSD,
    walletTotalUSD,
    hubCreditsGNRL,
    billedGpuHours,
    usdPerGpuHour,
    liveBids: liveBidsCount,
    providersOffered,
    hubCount,
    connectionCount,
    activeSpokeCount,
    liveSpokeCount,
    storageTotalGB,
    volumeCount,
    bidsPerProvider: providersOffered > 0 ? liveBidsCount / providersOffered : 0,
  };

  const providerCounts = new Map<string, number>();
  offers.forEach((o) => {
    const key = String(o.provider_name || o.source || o.provider_slug || "provider");
    providerCounts.set(key, (providerCounts.get(key) || 0) + 1);
  });
  const providerLiveRows: ProviderLiveRow[] = [...providerCounts.entries()]
    .map(([provider, bids]) => ({ provider, bids }))
    .sort((a, b) => b.bids - a.bids)
    .slice(0, 8);

  const executionRows: ExecutionRow[] = [
    { name: "Completed", value: jobsCompleted, color: "var(--chart-3)" },
    { name: "Running", value: jobsRunning, color: "var(--chart-1)" },
    { name: "Failed", value: jobsFailed, color: "var(--destructive)" },
    { name: "Queued", value: jobsQueued, color: "var(--chart-4)" },
  ];

  const byDay = new Map<string, number>();
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    byDay.set(key, 0);
  }
  jobs.forEach((j) => {
    const t = parseTime(j.updated_at || j.created_at);
    if (!t) return;
    const key = new Date(t).toISOString().slice(0, 10);
    if (!byDay.has(key)) return;
    byDay.set(key, (byDay.get(key) || 0) + 1);
  });
  const weeklyRows: WeeklyRow[] = [...byDay.entries()].map(([key, jobsCount]) => {
    const d = new Date(`${key}T00:00:00Z`);
    const day = d.toLocaleDateString(undefined, { weekday: "short" });
    return { day, jobs: jobsCount };
  });
  const peakDay = weeklyRows.reduce((best, row) => (row.jobs > best.jobs ? row : best), weeklyRows[0] || { day: "—", jobs: 0 });

  const usageByProviderMap = new Map<string, number>();
  jobs.forEach((j) => {
    const key = String(j.provisioned_provider || j.selected_provider || j.request?.selected_provider || "provider");
    usageByProviderMap.set(key, (usageByProviderMap.get(key) || 0) + safeNumber(j.billed_total_value));
  });
  const usageByProviderRows: UsageByProviderRow[] = [...usageByProviderMap.entries()]
    .map(([provider, spend]) => ({ provider, spend }))
    .sort((a, b) => b.spend - a.spend)
    .slice(0, 8);

  const storageByRegionMap = new Map<string, number>();
  storageVolumesRaw.forEach((v) => {
    const key = String(v.region_name || "global");
    storageByRegionMap.set(key, (storageByRegionMap.get(key) || 0) + safeNumber(v.size_gb));
  });
  const storageByRegionRows: StorageByRegionRow[] = [...storageByRegionMap.entries()]
    .map(([region, gb]) => ({ region, gb }))
    .sort((a, b) => b.gb - a.gb);

  const integrationRuns: IntegrationRunRow[] = [...jobs]
    .sort((a, b) => parseTime(b.updated_at || b.created_at) - parseTime(a.updated_at || a.created_at))
    .slice(0, 24)
    .map((j) => {
      const gpuType = String(j.provisioned_gpu_type || j.selected_gpu_type || j.request?.selected_gpu_type || "GPU");
      const gpuCount = safeNumber(j.provisioned_gpu_count || j.selected_gpu_count || j.request?.selected_gpu_count || 1);
      return {
        id: String(j.request?.job_id || "job"),
        state: jobStateKind(j.state),
        provider: String(j.provisioned_provider || j.selected_provider || j.request?.selected_provider || "provider"),
        gpu: `${gpuType}×${gpuCount}`,
        region: String(j.provisioned_region || j.selected_region || j.request?.selected_region || "global"),
        spent: safeNumber(j.billed_total_value),
        updated: relTime(j.updated_at || j.created_at),
      };
    });

  const liveBySpoke = new Map<string, number>();
  capacityEntries.forEach((c) => {
    const spoke = String(c.signal?.spoke_id || "");
    if (!spoke) return;
    liveBySpoke.set(spoke, safeNumber(c.signal?.avail_count));
  });

  const partners: PartnerRow[] = [...acks]
    .sort((a, b) => parseTime(b.last_state_change_at) - parseTime(a.last_state_change_at))
    .slice(0, 20)
    .map((a) => {
      const spokeID = String(a.spoke_id || "");
      const live = safeNumber(liveBySpoke.get(spokeID) || 0) > 0;
      return {
        name: String(a.ack_payload?.spoke_legal_entity?.legal_name || spokeID || a.spoke_peer_id || "peer"),
        region: String(a.ack_payload?.agreed_regions?.[0] || "global"),
        availability: live ? "live" : "idle",
        status: String(a.state || "").toUpperCase() === "ACTIVE" ? "connected" : "pending",
        lastChecked: relTime(a.last_state_change_at),
      };
    });

  const liveBids: BidRow[] = offers
    .slice()
    .sort((a, b) => safeNumber(b.stock_available) - safeNumber(a.stock_available))
    .slice(0, 80)
    .map((o) => ({
      gpu: String(o.gpu || o.flavor_name || "GPU"),
      source: String(o.provider_name || o.source || o.provider_slug || "provider"),
      location: String(o.region_name || "global"),
      capacity: safeNumber(o.stock_available),
      price: safeNumber(o.price_per_hour),
      currency: String(o.currency || "USD").toUpperCase(),
    }));

  const bestRateByProvider = new Map<string, number>();
  const setBestRate = (key: string, rate: number) => {
    const normalized = String(key || "").trim().toLowerCase();
    if (!normalized || !(rate > 0)) return;
    const current = bestRateByProvider.get(normalized);
    if (current == null || rate < current) bestRateByProvider.set(normalized, rate);
  };
  offers.forEach((o) => {
    const rate = safeNumber(o.price_per_hour);
    setBestRate(o.provider_slug || "", rate);
    setBestRate(o.source || "", rate);
    setBestRate(o.provider_name || "", rate);
  });

  const walletAccounts: WalletRow[] = walletAccountsRaw.map((a) => {
    const bestRate =
      bestRateByProvider.get(String(a.provider_slug || "").trim().toLowerCase()) ??
      bestRateByProvider.get(String(a.provider_name || "").trim().toLowerCase()) ??
      null;
    return {
      account: String(a.display_name || a.account_id || "account"),
      provider: String(a.provider_name || a.provider_slug || "provider"),
      credit: safeNumber(a.credit),
      currency: String(a.currency || "USD").toUpperCase(),
      bestRate,
      updated: relTime(a.fetched_at_utc),
    };
  });

  const storageVolumes: StorageRow[] = storageVolumesRaw
    .slice(0, 40)
    .map((v) => ({
      id: String(v.id || "volume"),
      region: String(v.region_name || "global"),
      type: String(v.volume_type || "volume"),
      gb: safeNumber(v.size_gb),
      lastWrite: relTime(v.updated_at),
    }));

  const activityFeed: ActivityRow[] = [
    ...jobs.slice(0, 20).map((j) => ({
      t: parseTime(j.updated_at || j.created_at),
      label: `Job ${String(j.request?.job_id || "job").slice(0, 12)} ${String(j.state || "").toLowerCase()}`,
    })),
    ...acks.slice(0, 20).map((a) => ({
      t: parseTime(a.last_state_change_at),
      label: `Connection ${String(a.spoke_id || a.spoke_peer_id || "peer")} ${String(a.state || "").toLowerCase()}`,
    })),
  ]
    .sort((a, b) => b.t - a.t)
    .slice(0, 14)
    .map((r) => ({
      time: r.t ? relTime(new Date(r.t).toISOString()) : "now",
      label: r.label,
    }));

  return {
    metrics,
    providerLiveRows,
    executionRows,
    weeklyRows,
    usageByProviderRows,
    storageByRegionRows,
    peakDay,
    integrationRuns,
    partners,
    liveBids,
    walletAccounts,
    activityFeed,
    storageVolumes,
  };
}

export function DashboardLiveProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<DashboardLiveData, "refresh">>(initialDashboardLiveData);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const serverCacheHydratedRef = useRef(false);
  const emptyHostedRefreshCountRef = useRef(0);

  const runRefresh = useCallback(async () => {
    const settings = readUISettings();
    const activeClientID = String(settings.tenant_id || readStoredAuthUserID() || "").trim();
    setState((prev) => ({ ...prev, loading: true }));

    if (isWalletCloudMode()) {
      const cachedPromise = readCloudWalletDashboardCache();
      const accountsPromise = readCloudWalletAccountsPublic()
        .then((accounts) => ({ accounts, failed: false }))
        .catch(() => ({ accounts: [], failed: true }));
      const cached = await cachedPromise;
      if (!serverCacheHydratedRef.current && cached && cached.accounts.length > 0) {
        const hosted = buildHostedWalletLiveData({
          accounts: cached.accounts,
          summary: cached.summary,
        });
        setState((prev) => ({
          ...prev,
          loading: true,
          error: "",
          fetchedAt: cached.updated_at || cached.summary.fetched_at_utc || prev.fetchedAt,
          metrics: hosted.metrics,
          walletAccounts: hosted.walletAccounts,
          activityFeed: hosted.activityFeed,
        }));
      }
      serverCacheHydratedRef.current = cached !== null;

      const accountResult = await accountsPromise;
      if (accountResult.failed) {
        setState((prev) => dashboardRefreshFailureState(prev, "Hosted wallet refresh is temporarily unavailable."));
        return;
      }
      const hostedPublicAccounts = accountResult.accounts;
      if (hostedPublicAccounts.length === 0) {
        emptyHostedRefreshCountRef.current += 1;
        if (emptyHostedRefreshCountRef.current >= 2) {
          const next = dashboardHostedEmptyState(EMPTY_DATA, emptyHostedRefreshCountRef.current);
          setState(next);
          cacheDashboardLiveData(next);
        } else {
          setState((previous) => dashboardHostedEmptyState(previous, emptyHostedRefreshCountRef.current));
        }
        return;
      }
      emptyHostedRefreshCountRef.current = 0;

      let summary: CloudWalletSummaryResponse | null = null;
      try {
        summary = await apiRequest<CloudWalletSummaryResponse>("/api/wallet/summary", {
          method: "POST",
          body: {},
          timeoutMs: 32000,
        }, settings);
      } catch {
        summary = null;
      }
      if (!summary) {
        setState((prev) => dashboardRefreshFailureState(prev, "Hosted wallet refresh is temporarily unavailable."));
        return;
      }

      const hosted = buildHostedWalletLiveData({
        accounts: hostedPublicAccounts,
        summary,
      });
      const next = {
        ...EMPTY_DATA,
        loading: false,
        error: "",
        fetchedAt: summary.fetched_at_utc || new Date().toISOString(),
        metrics: hosted.metrics,
        walletAccounts: hosted.walletAccounts,
        activityFeed: hosted.activityFeed,
      };
      setState(next);
      cacheDashboardLiveData(next);
      return;
    }

    const failed: string[] = [];
    const safe = async <T,>(label: string, op: Promise<T>, fallback: T): Promise<T> => {
      try {
        return await op;
      } catch {
        failed.push(label);
        return fallback;
      }
    };

    const [
      jobs,
      market,
      acks,
      capacity,
      runtimeHealth,
      walletSummary,
      storageVolumes,
      billingUsage,
      creditsBalance,
    ] = await Promise.all([
      safe("jobs", apiRequest<JobRecord[]>("/api/v1/jobs", { method: "GET", timeoutMs: 20000 }, settings), []),
      safe("market offers", apiRequest<{ offers?: MarketOffer[] }>("/api/v1/market/offers", { method: "GET", timeoutMs: 20000 }, settings), { offers: [] }),
      safe("acks", apiRequest<AckRecord[]>("/admin/v1/acks", { method: "GET", timeoutMs: 20000 }, settings), []),
      safe("capacity", apiRequest<{ entries?: CapacityEntry[] }>("/api/v1/capacity", { method: "GET", timeoutMs: 20000 }, settings), { entries: [] }),
      safe("runtime health", apiRequest<RuntimeHealth>("/api/v1/runtime/health", { method: "GET", timeoutMs: 10000 }, settings), {}),
      safe("wallet summary", apiRequest<WalletSummaryPayload>("/admin/v1/local/wallet/summary", { method: "GET", timeoutMs: 20000 }, settings), { accounts: [], total_credits_by_currency: [] }),
      safe("storage volumes", apiRequest<StorageVolumesPayload>("/api/v1/storage/volumes", { method: "GET", timeoutMs: 20000 }, settings), { volumes: [] }),
      safe("billing usage", apiRequest<BillingUsagePayload>(`/api/v1/billing/usage${activeClientID ? `?client_id=${encodeURIComponent(activeClientID)}` : ""}`, { method: "GET", timeoutMs: 20000 }, settings), { summary: {} }),
      safe("credits balance", apiRequest<CreditsBalancePayload>("/api/v1/credits/balance", { method: "GET", timeoutMs: 20000 }, settings), {}),
    ]);

    const derived = buildLiveData({
      activeClientID,
      jobs: Array.isArray(jobs) ? jobs : [],
      offers: Array.isArray(market.offers) ? market.offers : [],
      acks: Array.isArray(acks) ? acks : [],
      capacityEntries: Array.isArray(capacity.entries) ? capacity.entries : [],
      runtimeHealth: runtimeHealth || {},
      walletAccounts: Array.isArray(walletSummary.accounts) ? walletSummary.accounts : [],
      walletTotals: Array.isArray(walletSummary.total_credits_by_currency) ? walletSummary.total_credits_by_currency : [],
      storageVolumes: Array.isArray(storageVolumes.volumes) ? storageVolumes.volumes : [],
      billingUsageSummary: billingUsage.summary || {},
      creditsBalance: creditsBalance || {},
    });

    const next = {
      loading: false,
      error: failed.length ? `Some sources unavailable: ${failed.join(", ")}` : "",
      fetchedAt: new Date().toISOString(),
      ...derived,
    };
    if (failed.length > 0) {
      setState((prev) => prev.fetchedAt ? dashboardRefreshFailureState(prev, next.error) : next);
      return;
    }
    setState(next);
    cacheDashboardLiveData(next);
  }, []);

  const refresh = useCallback(() => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const pending = runRefresh().finally(() => {
      if (refreshInFlightRef.current === pending) refreshInFlightRef.current = null;
    });
    refreshInFlightRef.current = pending;
    return pending;
  }, [runRefresh]);

  useEffect(() => {
    void refresh();
    const sec = Math.max(5, Number(readUISettings().refresh_sec || 10));
    const id = window.setInterval(() => {
      void refresh();
    }, sec * 1000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const value = useMemo<DashboardLiveData>(() => ({
    ...state,
    refresh,
  }), [refresh, state]);

  return (
    <DashboardLiveContext.Provider value={value}>
      {children}
    </DashboardLiveContext.Provider>
  );
}

export function useDashboardLive() {
  const ctx = useContext(DashboardLiveContext);
  if (!ctx) throw new Error("useDashboardLive must be used inside <DashboardLiveProvider>");
  return ctx;
}
